/**
 * window.PPM.harness — the capture/profiling/playtest API. See ARCHITECTURE.md §7.
 *
 * EVERY SUBSYSTEM OWNER MUST KEEP THIS WORKING. If you break it, the critics cannot grade
 * the build, which is a worse bug than whatever you were fixing.
 *
 * Subsystems opt in to state forcing by implementing:
 *     applyHarnessState(patch)   // returns void; apply only the keys you own
 *     harnessStats()             // returns a small plain object merged into stats()
 *     freezeClocks()             // pin presentation clocks to sim time
 */

import * as THREE from 'three';

export function installHarness(game) {
  const ctx = game.ctx;

  let resolveReady;
  const ready = new Promise((res) => { resolveReady = res; });

  const detachedCam = { active: false };

  const api = {
    version: '1.0.0',
    isReady: false,
    ready,

    /** Full deterministic rebuild. Returns a promise. */
    async reset(opts = {}) {
      api.isReady = false;
      if (opts.seed != null) {
        game.seed = opts.seed;
        ctx.seed = opts.seed;
        ctx.rng = new (ctx.rng.constructor)(opts.seed);
      }
      for (const sys of game.systems.values()) {
        if (sys.isNull || typeof sys.reset !== 'function') continue;
        try { await sys.reset(opts); } catch (e) { console.error('[harness.reset]', sys.id, e); }
      }
      if (opts.weather) api.setState({ weather: opts.weather });
      if (opts.scenario) api.setState({ scenario: opts.scenario });
      await game.settleAssets();
      game.renderOnce();
      api.isReady = true;
    },

    /** Advance exactly n fixed simulation steps. No rendering. */
    step(n = 1) {
      for (let i = 0; i < n; i++) game.fixedStep();
      return ctx.time.sim;
    },

    /** Render exactly one frame at alpha = 0. */
    render() {
      game.renderOnce();
    },

    stepAndRender(n = 1) {
      api.step(n);
      game.renderOnce();
      return ctx.time.sim;
    },

    /**
     * Run n frames the way the real game does (step + presentation + render), recording
     * frame times. Used by the profiler, not by shot capture.
     */
    runFrames(n = 300, dtMs = 1000 / 60) {
      const t0 = performance.now();
      for (let i = 0; i < n; i++) {
        game.frame(t0 + i * dtMs);
      }
      return ctx.profiler.report();
    },

    /** Force game state for a shot. See ARCHITECTURE.md §7.1. */
    setState(patch = {}) {
      for (const sys of game.systems.values()) {
        if (sys.isNull || typeof sys.applyHarnessState !== 'function') continue;
        try { sys.applyHarnessState(patch); } catch (e) { console.error('[harness.setState]', sys.id, e); }
      }
      if (ctx.materials && typeof ctx.materials.applyHarnessState === 'function') {
        try { ctx.materials.applyHarnessState(patch); } catch (e) { console.error('[harness.setState] materials', e); }
      }
      if (patch.camera) api.camera(patch.camera);
      return true;
    },

    /**
     * Detach the chase camera and pin it. Pass null to reattach.
     * `vehicle` must honour ctx.camera.userData.harnessDetached and stop driving it.
     */
    camera(pose) {
      if (!pose) {
        detachedCam.active = false;
        ctx.camera.userData.harnessDetached = false;
        return;
      }
      detachedCam.active = true;
      ctx.camera.userData.harnessDetached = true;
      if (pose.position) ctx.camera.position.set(pose.position[0], pose.position[1], pose.position[2]);
      if (pose.lookAt) ctx.camera.lookAt(pose.lookAt[0], pose.lookAt[1], pose.lookAt[2]);
      if (pose.quaternion) ctx.camera.quaternion.set(...pose.quaternion);
      if (pose.fov) { ctx.camera.fov = pose.fov; ctx.camera.updateProjectionMatrix(); }
      ctx.camera.updateMatrixWorld(true);
    },

    /** Deterministic input tape. See ARCHITECTURE.md §7.2. */
    scriptInput(tape) {
      ctx.input.setScript(tape);
      return true;
    },

    scriptFinished() { return ctx.input.scriptFinished; },

    metrics() { return ctx.profiler.report(); },
    rawFrames() { return ctx.profiler.rawFrames(); },
    resetMetrics() { ctx.profiler.reset(); },

    stats() {
      const out = {
        sim: +ctx.time.sim.toFixed(4),
        frame: ctx.time.frame,
        drawCalls: ctx.profiler.drawCalls,
        triangles: ctx.profiler.triangles,
        programs: ctx.profiler.programs,
        meshes: countMeshes(ctx.scene),
        materialsFallback: !!(ctx.materials && ctx.materials.isFallback),
        /** Per-subsystem init() cost in ms. The frame profiler cannot see this. */
        initTimings: game.initTimings || {},
        initTotalMs: Object.values(game.initTimings || {}).reduce((a, b) => a + b, 0),
        subsystems: {},
      };
      for (const [id, sys] of game.systems) {
        // 'stub' must be distinguishable from 'live' — reporting a scaffolded stub as live
        // misleads both agents and critics about what is actually in the frame.
        out.subsystems[id] = sys.isNull ? 'null' : (sys.stub ? 'stub' : 'live');
        if (!sys.isNull && typeof sys.harnessStats === 'function') {
          try { Object.assign(out, { [id]: sys.harnessStats() }); } catch { /* ignore */ }
        }
      }
      return out;
    },

    async settleAssets() { await game.settleAssets(); },

    freezeClocks() { game.freezeClocks(); return true; },

    /** Event log helpers for the scripted playtest critic. */
    startEventLog() { ctx.bus.log = []; return true; },
    eventLog() { return ctx.bus.log ? ctx.bus.log.slice() : []; },
    stopEventLog() { const l = ctx.bus.log; ctx.bus.log = null; return l || []; },

    /** Deterministic self-check: same seed + same steps must give the same RNG state. */
    rngState() { return ctx.rng.getState(); },

    /** Escape hatch for critics that need to poke at something specific. */
    _ctx: ctx,
    _game: game,
  };

  window.PPM = window.PPM || {};
  window.PPM.version = '1.0.0';
  window.PPM.seedDefault = game.seed;
  window.PPM.harness = api;
  window.PPM.THREE = THREE;

  // Ready when one full frame has been drawn.
  requestAnimationFrame(() => {
    api.isReady = true;
    resolveReady();
    window.__PPM_READY__ = true;
    document.documentElement.setAttribute('data-ppm-ready', '1');
  });

  return api;
}

function countMeshes(scene) {
  let n = 0;
  scene.traverse((o) => { if (o.isMesh || o.isInstancedMesh || o.isPoints || o.isLine) n++; });
  return n;
}
